import { useT } from '@/i18n';
import { formatMoney, inputToMinor } from '@/utils/money';
import type { SplitBillBody, TableSession } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Segmented,
  Stepper,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import {
  useAddSeat,
  useAssignItemSeat,
  useSplitBill,
  useTableSession,
} from '../hooks/useTableOrdering';

type SplitMode = 'EVEN' | 'BY_AMOUNT' | 'BY_SEAT';

/**
 * Step 1 of settling - build the split. A focused page (one job): pick a mode
 * and fill it in. On success it returns to the bill hub, where the operator
 * then takes payment against each share. Pre-seeds from the guest's suggestion
 * when there is one.
 */
export default function BillSplitPage({ f7route }: { f7route: { params: { sessionId: string } } }) {
  const tAll = useT();
  const t = tAll.tableOrdering.billSplit;
  const sessionId = f7route.params.sessionId;
  const { data: session, isLoading } = useTableSession(sessionId);

  return (
    <Page name="bill-split">
      <Navbar title={t.title} backLink={tAll.back} />
      {isLoading || !session ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : (
        <SplitBuilder session={session} />
      )}
    </Page>
  );
}

function SplitBuilder({ session }: { session: TableSession }) {
  const tAll = useT();
  const t = tAll.tableOrdering.billSplit;
  const tAddRound = tAll.tableOrdering.addRound;
  const splitBill = useSplitBill();
  const assignItemSeat = useAssignItemSeat();
  const addSeat = useAddSeat();
  const currency = session.currency;
  const suggested = session.proposedSplit;
  const seats = session.seats;

  const seatLabelFor = (seatId: string | null): string => {
    if (seatId === null) return tAddRound.shared;
    const s = seats.find((x) => x.id === seatId);
    return s ? (s.label ?? t.seatFallback(s.ordinal)) : tAddRound.shared;
  };

  // Live items with their seat (non-voided, non-cancelled) for the roster.
  const seatItems: { id: string; seatId: string | null; label: string; cents: number }[] = [];
  for (const r of session.rounds) {
    if (r.status === 'CANCELLED') continue;
    for (const it of r.items) {
      if (it.voidedAt) continue;
      seatItems.push({
        id: it.id,
        seatId: it.seatId,
        label: `${it.quantity}× ${it.nameSnapshot}`,
        cents: it.lineTotalCents,
      });
    }
  }
  const ownSubtotal = (seatId: string | null) =>
    seatItems.filter((it) => it.seatId === seatId).reduce((s, it) => s + it.cents, 0);
  const sharedTotal = ownSubtotal(null);

  const [mode, setMode] = useState<SplitMode>(
    suggested?.mode === 'BY_AMOUNT' ? 'BY_AMOUNT' : 'EVEN',
  );
  const [ways, setWays] = useState(
    suggested?.mode === 'EVEN' && suggested.ways ? suggested.ways : 2,
  );
  const [amounts, setAmounts] = useState<string[]>(['', '']);

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  function assignSeat(itemId: string, seatId: string | null) {
    assignItemSeat.mutate(
      { sessionId: session.id, itemId, body: { seatId } },
      { onSuccess: () => toast(t.movedToSeat(seatLabelFor(seatId))) },
    );
  }

  function addSeatThenAssign(itemId: string) {
    addSeat.mutate(
      { sessionId: session.id, body: {} },
      {
        onSuccess: (s) => {
          const newest = [...s.seats].sort((a, b) => b.ordinal - a.ordinal)[0];
          if (newest) assignSeat(itemId, newest.id);
        },
      },
    );
  }

  function openReassign(itemId: string, currentSeatId: string | null) {
    const tick = (on: boolean) => (on ? '  ✓' : '');
    f7.actions
      .create({
        buttons: [
          [
            { text: t.moveItem, label: true },
            ...seats.map((s) => ({
              text: `${s.label ?? t.seatFallback(s.ordinal)}${tick(currentSeatId === s.id)}`,
              onClick: () => assignSeat(itemId, s.id),
            })),
            {
              text: `${tAddRound.shared}${tick(currentSeatId === null)}`,
              onClick: () => assignSeat(itemId, null),
            },
            { text: tAddRound.addSeat, strong: true, onClick: () => addSeatThenAssign(itemId) },
          ],
          [{ text: tAll.cancel, color: 'red' }],
        ],
      })
      .open();
  }

  function addStandaloneSeat() {
    addSeat.mutate({ sessionId: session.id, body: {} }, { onSuccess: () => toast(t.seatAdded) });
  }

  function createSplit() {
    let body: SplitBillBody;
    if (mode === 'BY_SEAT') {
      if (seats.length === 0) return toast(t.atLeastOneSeat);
      body = { mode: 'BY_SEAT' };
    } else if (mode === 'EVEN') {
      body = { mode: 'EVEN', ways };
    } else {
      const parties = amounts
        .map((a) => inputToMinor(a, currency))
        .filter((c) => c > 0)
        .map((amountCents) => ({ amountCents }));
      if (parties.length < 2) return toast(t.atleastTwoAmounts);
      body = { mode: 'BY_AMOUNT', parties };
    }
    splitBill.mutate(
      { sessionId: session.id, body },
      {
        onSuccess: () => {
          toast(t.splitCreated);
          f7.views.current.router.back();
        },
      },
    );
  }

  return (
    <div className="to-bill">
      <Block className="to-bill-lead">
        <span>{t.billTotal}</span>
        <b>{formatMoney(session.totalCents, currency)}</b>
      </Block>

      <Block>
        <Segmented strong round>
          <Button round active={mode === 'EVEN'} onClick={() => setMode('EVEN')}>
            {t.even}
          </Button>
          <Button round active={mode === 'BY_AMOUNT'} onClick={() => setMode('BY_AMOUNT')}>
            {t.byAmount}
          </Button>
          <Button round active={mode === 'BY_SEAT'} onClick={() => setMode('BY_SEAT')}>
            {t.bySeat}
          </Button>
        </Segmented>
      </Block>

      {mode === 'BY_SEAT' && (
        <>
          {seats.length === 0 ? (
            <Block className="text-align-center text-color-gray" style={{ paddingTop: 8 }}>
              {t.noSeats}
              <div style={{ marginTop: 14 }}>
                <Button round fill onClick={addStandaloneSeat}>
                  {tAddRound.addSeat}
                </Button>
              </div>
            </Block>
          ) : (
            <>
              {seats.map((seat) => {
                const own = seatItems.filter((it) => it.seatId === seat.id);
                return (
                  <div key={seat.id}>
                    <BlockTitle>
                      <span className="to-seat-grp">
                        <span>{seat.label ?? t.seatFallback(seat.ordinal)}</span>
                        <span className="to-seat-grp__sum">
                          {formatMoney(ownSubtotal(seat.id), currency)}
                        </span>
                      </span>
                    </BlockTitle>
                    <List dividers strongIos insetIos>
                      {own.length === 0 ? (
                        <ListItem title={t.noItems} className="text-color-gray" />
                      ) : (
                        own.map((it) => (
                          <ListItem
                            key={it.id}
                            link="#"
                            noChevron
                            title={it.label}
                            after={formatMoney(it.cents, currency)}
                            onClick={() => openReassign(it.id, it.seatId)}
                          />
                        ))
                      )}
                    </List>
                  </div>
                );
              })}

              <BlockTitle>
                <span className="to-seat-grp">
                  <span>{tAddRound.shared}</span>
                  <span className="to-seat-grp__sum">{formatMoney(sharedTotal, currency)}</span>
                </span>
              </BlockTitle>
              <List dividers strongIos insetIos>
                {seatItems.filter((it) => it.seatId === null).length === 0 ? (
                  <ListItem title={t.nothingShared} className="text-color-gray" />
                ) : (
                  seatItems
                    .filter((it) => it.seatId === null)
                    .map((it) => (
                      <ListItem
                        key={it.id}
                        link="#"
                        noChevron
                        title={it.label}
                        after={formatMoney(it.cents, currency)}
                        onClick={() => openReassign(it.id, it.seatId)}
                      />
                    ))
                )}
              </List>

              <Block className="to-seat-note text-color-gray">
                {t.splitNote(seats.length)}
                <div style={{ marginTop: 12 }}>
                  <Button round onClick={addStandaloneSeat} className="to-bill-act">
                    {tAddRound.addSeat}
                  </Button>
                </div>
              </Block>
            </>
          )}
        </>
      )}

      {mode === 'EVEN' && (
        <Block>
          <div className="to-bill-even">
            <div className="to-bill-even__per">
              <b>{formatMoney(Math.ceil(session.totalCents / ways), currency)}</b>
              <span>{t.perPersonWays(ways)}</span>
            </div>
            <Stepper value={ways} min={2} max={20} onStepperChange={(v: number) => setWays(v)} />
          </div>
        </Block>
      )}

      {mode === 'BY_AMOUNT' && (
        <>
          <List dividers strongIos insetIos>
            {amounts.map((a, i) => (
              <ListItem key={i} swipeout={amounts.length > 2} title={t.partyLabel(i + 1)}>
                <input
                  slot="after"
                  type="number"
                  inputMode="decimal"
                  className="to-amount-field"
                  placeholder="0.00"
                  value={a}
                  onChange={(e) =>
                    setAmounts((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                  }
                />
                {amounts.length > 2 && (
                  <div slot="swipeout-actions-right">
                    <button
                      type="button"
                      className="swipeout-close color-red"
                      onClick={() => setAmounts((prev) => prev.filter((_, j) => j !== i))}
                    >
                      {tAll.delete}
                    </button>
                  </div>
                )}
              </ListItem>
            ))}
          </List>
          <List dividers strongIos insetIos>
            <ListItem link="#" title={t.addParty} onClick={() => setAmounts((p) => [...p, ''])}>
              <i slot="media" className="f7-icons to-bill-nav-ic">
                plus_circle
              </i>
            </ListItem>
          </List>
        </>
      )}

      <Block>
        <Button
          fill
          large
          round
          disabled={splitBill.isPending || (mode === 'BY_SEAT' && seats.length === 0)}
          onClick={createSplit}
        >
          {t.createSplit}
        </Button>
      </Block>
      <div style={{ height: 'calc(16px + var(--f7-safe-area-bottom, 0px))' }} />
    </div>
  );
}
